import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import type { DefaultOptionProps } from 'vopay';
import moment from 'moment';
import type { TransactionHistoryStatus } from 'vopay';
import { format, getTime, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import usePostConfirmTransaction from '@/hooks/useApi/usePostConfirmTransaction';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import TransactionSearchForm from '@/components/Transaction/TransactionsHistory/TransactionSearchForm';
import ActivityType from '../ActivityType';
import type { ActivityTabType } from '../ActivityTab';
import { TableFiltersContainer, CopyButton, Truncate } from 'UI';
import styles from '@/components/Transaction/TransactionsHistory/Transactions.module.scss';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';

const {
  Table,
  Box,
  Tooltip,
  Button,
  MaterialSymbol,
  Pagination,
  formattedCurrency,
  withStyles,
  capitalizeFirstLetter,
  usePagination,
  useUpdateQueryParams,
} = vp;

const cn = withStyles(styles);

type QueryParams = {
  type?: string;
  status?: TransactionHistoryStatus | null;
  flagged?: string;
  currency?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  cardTab?: string;
  settlementDate?: string | null;
  activityType?: ActivityTabType;
};

type TransactionParams = {
  startDate: string;
  endDate: string;
  status: TransactionHistoryStatus | 'All' | null;
  flagged: string;
  type: string;
  currency: string;
  hasInvalidEndDate: boolean;
  searchTerm: string;
  timeZone: string;
  settlementDate?: string;
  offset?: number;
};

type TransactionHistoryTableProps = {
  active: boolean;
  accountID: string;
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
};

const TransactionHistoryTable: FunctionComponent<TransactionHistoryTableProps> = ({
  active,
  accountID,
  activityTypeOptions,
  currentType,
  handleCurrentType,
}) => {
  const alertDialog = useAlertDialog();
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionHistory' });
  const { t: tGlobal } = useTranslation('translations');
  const {
    response: { Country, clientAccountsEnabled },
  } = useClientData();
  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const { balances } = useAccountBalances();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const settlementDate = useMemo(() => queryParams.get('settlementDate') || null, [queryParams]);

  const initialStartDate = moment().subtract(7, 'day').format('YYYY-MM-DD');
  const initialEndDate = moment().format('YYYY-MM-DD');
  const startDate = useMemo(() => queryParams.get('startDate') || initialStartDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || initialEndDate || '', [queryParams]);
  const currentTimeZone = useMemo(
    () => (queryParams.get('timeZone') as vp.Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );
  const type = useMemo(() => queryParams.get('type') || '', [queryParams]);
  const status = useMemo(() => (queryParams.get('status') as TransactionHistoryStatus) || null, [queryParams]);
  const flagged = useMemo(() => queryParams.get('flagged') || '', [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || 'All', [queryParams, Country]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Boolean(
      type ||
        status ||
        flagged ||
        searchTerm ||
        (currency && currency !== 'All') ||
        (currentTimeZone && currentTimeZone !== (userSettings.timezone || 'UTC')) ||
        (startDate && startDate !== initialStartDate) ||
        (endDate && endDate !== initialEndDate),
    );
  }, [
    type,
    status,
    flagged,
    searchTerm,
    currency,
    Country,
    currentTimeZone,
    userSettings.timezone,
    startDate,
    endDate,
    initialStartDate,
    initialEndDate,
  ]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [filteredLists, setFilteredLists] = useState<vp.TransactionsLists[]>([]);
  const [sortedList, setSortedList] = useState<vp.TransactionsLists[]>([]);
  const [sort, setSort] = useState<{
    id: string;
    order: 'asc' | 'desc';
  } | null>(null);

  const { fetchTransactionsHistory, totalRecords } = useFetchTransactionsHistory();
  const [, , , postConfirmTransaction] = usePostConfirmTransaction({});

  // pagination
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchTransactionList = async ({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    status: selectedStatus,
    flagged: isFlaggedSelected,
    type: selectedType,
    currency: selectedCurrency,
    hasInvalidEndDate: isEndDateInvalid,
    searchTerm,
    timeZone,
    offset = 0,
    settlementDate,
  }: TransactionParams) => {
    const transactionRequestPayload = {
      StartDateTime: selectedStartDate,
      EndDateTime: selectedEndDate,
      TransactionStatus: selectedStatus === null || selectedStatus === 'All' ? undefined : selectedStatus,
      IsFlagged: isFlaggedSelected === 'All' || isFlaggedSelected === '' ? undefined : isFlaggedSelected === 'Yes',
      TransactionType:
        selectedType === 'All' ||
        selectedType === '' ||
        selectedType === 'Refund' ||
        selectedType === 'Auto Balance Transfer'
          ? undefined
          : selectedType,
      Currency: selectedCurrency,
      SearchText: searchTerm,
      Timezone: timeZone,
      SettlementDate: settlementDate,
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: accountID,
    };

    if (isEndDateInvalid) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchTransactionsHistory(transactionRequestPayload);

      if (!res.Success) throw new Error(res.ErrorMessage);

      setFilteredLists(
        res.Transactions.map((transaction) => ({
          ...transaction,
          TransactionType: ModifyType(transaction.TransactionType, transaction.Notes),
        })),
      );

      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching transaction history';
      alertDialog.error('Error', msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    const formattedSettlementDate = settlementDate ? format(parseISO(settlementDate), 'yyyy-MM-dd') : null;
    fetchTransactionList({
      startDate,
      endDate,
      status,
      flagged,
      type,
      currency,
      hasInvalidEndDate: false,
      searchTerm,
      timeZone: currentTimeZone,
      settlementDate: formattedSettlementDate || undefined,
      offset,
    });

    updateQueryParams({
      cardTab: 'activity',
      activityType: 'Transaction History',
    });
  }, []);

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    if (sort) {
      const { id, order } = sort;
      if (id === 'transactionDate') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) =>
            moment(a.TransactionDateTime).isAfter(moment(b.TransactionDateTime)) ? 1 : -1,
          );
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) =>
            moment(a.TransactionDateTime).isBefore(moment(b.TransactionDateTime)) ? 1 : -1,
          );
          setSortedList(sorted);
        }
      }
      if (id === 'transId') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (a.TransactionID > b.TransactionID ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (a.TransactionID < b.TransactionID ? 1 : -1));
          setSortedList(sorted);
        }
      }
      if (id === 'name') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (a.FullName > b.FullName ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (a.FullName < b.FullName ? 1 : -1));
          setSortedList(sorted);
        }
      }
      if (id === 'type') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (a.TransactionType > b.TransactionType ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (a.TransactionType < b.TransactionType ? 1 : -1));
          setSortedList(sorted);
        }
      }
      if (id === 'referenceNumber') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (a.ClientReferenceNumber > b.ClientReferenceNumber ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (a.ClientReferenceNumber < b.ClientReferenceNumber ? 1 : -1));
          setSortedList(sorted);
        }
      }
      if (id === 'credit') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) =>
            parseFloat(a.CreditAmount) > parseFloat(b.CreditAmount) ? 1 : -1,
          );
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) =>
            parseFloat(a.CreditAmount) < parseFloat(b.CreditAmount) ? 1 : -1,
          );
          setSortedList(sorted);
        }
      }
      if (id === 'debit') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (parseFloat(a.DebitAmount) > parseFloat(b.DebitAmount) ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (parseFloat(a.DebitAmount) < parseFloat(b.DebitAmount) ? 1 : -1));
          setSortedList(sorted);
        }
      }
      if (id === 'status') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (a.TransactionStatus > b.TransactionStatus ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (a.TransactionStatus < b.TransactionStatus ? 1 : -1));
          setSortedList(sorted);
        }
      }
      if (id === 'notes') {
        if (order === 'asc') {
          const sorted = filteredLists.sort((a, b) => (a.Notes > b.Notes ? 1 : -1));
          setSortedList(sorted);
        } else if (order === 'desc') {
          const sorted = filteredLists.sort((a, b) => (a.Notes < b.Notes ? 1 : -1));
          setSortedList(sorted);
        }
      }
    } else {
      setSortedList(filteredLists);
    }
  }, [filteredLists, sort]);

  const tableProps: vp.TableProps<vp.TransactionsLists> = {
    id: 'transactionHistoryTable',
    version: '2',
    onSort: (params) => {
      setSort(params);
    },
    zebra: true,
    columns: [
      {
        id: 'transactionDate',
        header: t('table.transactionDate'),
        align: 'left',
        sortable: true,

        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <Box gap="sm" ai="center">
              {transaction.data.TransactionFlag !== '' && (
                <Tooltip
                  information={transaction.data.TransactionFlag}
                  hasButton={false}
                  customElement={
                    <Button
                      isDisabled={!active}
                      color="none"
                      background="transparent"
                      label={<MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />}
                      noPadding
                      noShadow
                      onClick={async () => {
                        const result = await alertDialog.caution(
                          t('dialogue.title'),
                          <Box gap="sm">
                            {transaction.data.TransactionFlag} <CopyButton content={transaction.data.TransactionFlag} />
                          </Box>,
                          {
                            abortLabel: t('dialogue.closeButton'),
                            confirmLabel: t('dialogue.removeFlagButton'),
                          },
                        );

                        if (result === 'confirm') {
                          const res = await postConfirmTransaction({
                            TransactionID: transaction.data.TransactionID,
                          });

                          if (res.Success) {
                            fetchTransactionList({
                              startDate,
                              endDate,
                              status,
                              flagged,
                              type,
                              currency,
                              hasInvalidEndDate: false,
                              searchTerm,
                              timeZone: currentTimeZone,
                            });
                          }
                        }
                      }}
                    />
                  }
                  position="right"
                />
              )}
              <span>{transaction.data.TransactionDateTime}</span>
            </Box>
          );
        },
      },
      {
        id: 'transId',
        header: t('table.id'),
        align: 'left',
        sortable: true,
        style: {
          minWidth: '100px',
        },
        template: ({ data }) => {
          return (
            <Box gap="sm" ai="center">
              <Link
                className={styles.transactionIDLink}
                to={{
                  pathname: `/transactions-history/details/${vp.removeWhiteSpaceURI(data.TransactionType)}/${
                    data.TransactionID
                  }/${data.Currency}`,
                  state: {
                    transactionId: data.TransactionID,
                    hasErrorInfo: data.TransactionStatus === 'failed',
                    failureReason: data.TransactionFailureReason,
                    clientAccountId: data.ClientAccountID,
                  },
                }}
              >
                {data.TransactionID}
              </Link>
              <CopyButton content={data.TransactionID} color="rgba(45, 45, 45, 0.6)" />
            </Box>
          );
        },
      },
      {
        id: 'name',
        header: t('table.name'),
        align: 'left',
        field: 'FullName',
        sortable: true,
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'type',
        header: t('table.type'),
        align: 'left',
        sortable: true,
        template: (transaction) => {
          return tGlobal(`Transaction.TransactionHistory.transactionTypes.${transaction.data.TransactionType}`, {
            defaultValue: transaction.data.TransactionType,
          });
        },
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'paymentRailID',
        header: t('table.paymentRailID', { defaultValue: 'Payment Rail ID' }),
        align: 'left',
        field: 'PaymentRailID',
        sortable: true,
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'referenceNumber',
        header: t('table.referenceNumber'),
        align: 'left',
        field: 'ClientReferenceNumber',
        sortable: true,
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'credit',
        header: t('table.credit'),
        align: 'left',
        sortable: true,
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.CreditAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`${transaction.data.Currency} ${formattedCurrency(transaction.data.CreditAmount)}`}</span>
                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('table.pending')}{' '}
                      {`${transaction.data.Currency} ${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'debit',
        header: t('table.debit'),
        align: 'left',
        sortable: true,
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.DebitAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`${transaction.data.Currency} ${formattedCurrency(transaction.data.DebitAmount)}`}</span>
                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('table.pending')}{' '}
                      {`${transaction.data.Currency} ${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: t('table.status'),
        align: 'left',
        sortable: true,
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <Box gapSize="10px">
              <TransactionStatus
                status={transaction.data.TransactionStatus}
                label={
                  capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${transaction.data.TransactionStatus}`, {
                      defaultValue: transaction.data.TransactionStatus,
                    }),
                  ) || ''
                }
              />
              {transaction.data.TransactionFailureReason && (
                <Tooltip
                  information={transaction.data.TransactionFailureReason}
                  icon="info"
                  variant="outlined"
                  iconWidth="14px"
                  buttonWidth="22px"
                  hasButton={true}
                  position="bottomLeft"
                />
              )}
            </Box>
          );
        },
      },
      {
        id: 'settlementDate',
        header: t('table.settlementDate'),
        align: 'left',
        field: 'SettlementDate',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'notes',
        header: t('table.notes'),
        align: 'left',
        sortable: true,
        style: {
          minWidth: '220px',
        },
        template: (transaction) => {
          const isTruncate = transaction.data.Notes.length > 25;

          return (
            <>
              <Tooltip
                information={transaction.data.Notes}
                isDisabled={!isTruncate}
                hasButton={false}
                customElement={
                  <Box gap="sm" ai="center" jc="sb" className="notes">
                    <Truncate text={transaction.data.Notes} maxLength={25} truncateEnding="..." />
                    {transaction.data.Notes && (
                      <CopyButton content={transaction.data.Notes} color="rgba(45, 45, 45, 0.6)" />
                    )}
                  </Box>
                }
                position="bottomLeft"
              />
            </>
          );
        },
      },
    ],
    rows:
      sortedList.map((transaction): { id: string; data: vp.TransactionsLists } => ({
        id: transaction.TransactionID,
        data: transaction,
      })) || [],
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
          <TableFiltersContainer.Trigger />
        </Box>
        <TableFiltersContainer.Content>
          <TransactionSearchForm
            type={type}
            status={status}
            flagged={flagged}
            currency={currency}
            searchTerm={searchTerm}
            timeZone={currentTimeZone}
            includeSubAccounts={false}
            balances={balances}
            settlementDate={
              (settlementDate ? getTime(parseISO(settlementDate)) : undefined) as vp.Timestamp | undefined
            }
            initialTimeRange={[
              parseISO(startDate).getTime() as vp.Timestamp,
              parseISO(endDate).getTime() as vp.Timestamp,
            ]}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            onSearch={(searchParams) => {
              const formattedSettlementDate = searchParams.settlementDate
                ? format(new Date(searchParams.settlementDate), 'yyyy-MM-dd')
                : undefined;
              updateQueryParams({
                type: searchParams.type,
                status: searchParams.status,
                flagged: searchParams.flagged,
                currency: searchParams.currency,
                searchTerm: searchParams.searchTerm,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                settlementDate: formattedSettlementDate,
                timeZone: searchParams.timeZone,
              });

              fetchTransactionList({
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                status: searchParams.status,
                flagged: searchParams.flagged,
                type: searchParams.type,
                currency: searchParams.currency,
                hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                settlementDate: formattedSettlementDate,
                searchTerm: searchParams.searchTerm,
                timeZone: searchParams.timeZone,
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={cn('tableContainer', 'scrollbar')}>
        <Table {...tableProps} />
      </div>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              fetchTransactionList({
                startDate,
                endDate,
                status,
                flagged,
                type,
                currency,
                hasInvalidEndDate: startDate > endDate,
                searchTerm,
                timeZone: currentTimeZone,
                offset,
              });
            }}
          />
        )}
      </Box>
      {initialLoad && filteredLists && filteredLists.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('transactions.noRecords')}
          </Box>
        </Box>
      )}
    </Box>
  );

  function ModifyType(transactionType: vp.TransactionTypesOptions, notes: string): vp.TransactionTypesOptions {
    if (
      (transactionType === 'EFT Withdrawal' || transactionType === 'ACH Withdrawal') &&
      notes === 'Scheduled Auto Balance Transfer'
    ) {
      return 'Auto Balance Transfer';
    }
    return transactionType;
  }
};
export default TransactionHistoryTable;
